import {lengthValidator, maxLengthValidator} from "./validators";

enum LogLevel {
    DEBUG = 10000,
    INFO = 20000,
    WARN = 30000,
    ERROR = 40000,
    OFF = Number.MAX_SAFE_INTEGER
}

type LogSink = (context: LoggingContext) => void;

const NO_OP_LOG_SINK: LogSink = () => {};
const namespaceLengthValidator = maxLengthValidator(100);
const messageLengthValidator = lengthValidator(1, 250);


/**
 * The log sinks for the console
 */
const CONSOLE_LOG_SINKS: Record<LogLevel, LogSink> = {
    [LogLevel.OFF]: NO_OP_LOG_SINK,
    [LogLevel.DEBUG]: (context: LoggingContext) => console.debug(`[${context.namespace}] ${context.message}`),
    [LogLevel.INFO]: (context: LoggingContext) => console.info(`[${context.namespace}] ${context.message}`),
    [LogLevel.WARN]: (context: LoggingContext) => console.warn(`[${context.namespace}] ${context.message}`),
    [LogLevel.ERROR]: (context: LoggingContext) => console.error(`[${context.namespace}] ${context.message}`)
};

export interface LoggingContext {
    /** Optional: namespace, mainly for filtering */
    namespace?: string;
    /** The main message */
    message: string;
    /** Optional: the error object, includes the stacktrace */
    error?: Error;
}

/**
 * Logs a debug message
 * @param context the context of logging
 */
export const debug = (context: LoggingContext): void => {
   log(context, LogLevel.DEBUG);
};

/**
 * Logs an info message
 * @param context the context of logging
 */
export const info = (context: LoggingContext): void => {
    log(context, LogLevel.INFO);
};

/**
 * Logs a warning message
 * @param context the context of the logging
 */
export const warn = (context: LoggingContext): void => {
   log(context, LogLevel.WARN);
};

/**
 * Logs an error message
 * @param context the context of the logging
 */
export const error = (context: LoggingContext): void => {
    log(context, LogLevel.ERROR);
};

/**
 * Logs a message based on log level
 * @param context the context
 * @param logLevel the log level
 */
const log = (context: LoggingContext, logLevel: LogLevel): void => {
    const namespaceLengthValid: boolean = namespaceLengthValidator(context.namespace);
    const messageLengthValid: boolean = messageLengthValidator(context.message);
    if(!namespaceLengthValid || !messageLengthValid) {
        return;
    }
    const logSink: LogSink = getLogSinkBy(logLevel);
    logSink(context);
};

/**
 * Getting the appropriate log sink based on log level and environment
 * @param logLevel the log level
 */
const getLogSinkBy = (logLevel: LogLevel): LogSink => {
    if (window['__isDev']) {
        return CONSOLE_LOG_SINKS[logLevel] ?? NO_OP_LOG_SINK
    }

    const configuredLevel: LogLevel = getConfiguredLevel();
    if (logLevel >= configuredLevel) {
        return (context: LoggingContext) => publishLog(logLevel, context);
    }
    return NO_OP_LOG_SINK;
};

/**
 * This function will send the log to the browser bff via sendBeacon
 * @param logLevel the log level
 * @param context the context of the logging
 */
const publishLog = (logLevel: LogLevel, context: LoggingContext): void => {
    const headers: Record<string, string> = {type: 'application/json'};
    const json: string = context ? JSON.stringify({
        namespace: context.namespace,
        message: context.message,
        errorMessage: context.error?.message?.substring(0, 100),
        errorName: context.error?.name?.substring(0, 50),
        errorStack: context.error?.stack?.substring(0, 500)
    }) : '{}';
    const dataBlob: Blob = new Blob([json], headers);
    const url: string = constructLogUrl(logLevel);
    if (url) {
        navigator.sendBeacon(url, dataBlob);
    }
};

const constructLogUrl = (logLevel: LogLevel): string => {
    const baseUrl: string = window['bpMetaInfo']?.["bonprix:loggingUrl"]
    return baseUrl ? baseUrl + LogLevel[logLevel] + '/' : undefined;
};

const getConfiguredLevel = (): LogLevel => {
    const lvlSeverity: string = window['bpMetaInfo']?.["bonprix:loggingLevelSeverity"];
    const lvlNumber: number = parseInt(lvlSeverity, 10);
    const logLevelElement = LogLevel[lvlNumber];
    return LogLevel[logLevelElement] ?? LogLevel.OFF;
};
